import { isHttpError } from "@/shared/api/errors"
import {
	luniVoiceMessageDelete,
	luniVoiceMessageFinish,
	luniVoiceMessageGet,
	luniVoiceMessageHold,
	luniVoiceMessagePublish,
	luniVoiceMessageRelease,
	luniVoiceMessageUrl,
	type MessageWithUrl,
} from "@/shared/api/generated-social"
import { zMessageBody } from "@/shared/api/generated-social/zod.gen"
import { deadlineFetch } from "@/shared/api/http"
import type { HoldResult, VoiceMessage, VoiceUrl } from "../model/types"

const PUBLISH_DEADLINE_MS = 60_000

export async function getVoiceMessage(
	friendId: number,
): Promise<MessageWithUrl> {
	return luniVoiceMessageGet({
		query: { friend_id: friendId },
	})
}

export async function holdVoiceMessage(input: {
	friendId: number
}): Promise<HoldResult> {
	return luniVoiceMessageHold({
		body: { friend_id: input.friendId },
	})
}

export async function releaseVoiceMessage(input: {
	holdId: string
}): Promise<VoiceMessage> {
	const { message } = await luniVoiceMessageRelease({
		body: { hold_id: input.holdId },
	})
	return message
}

export async function publishVoiceMessage(input: {
	holdId: string
	clientMessageId: string
	file: Blob
	fileName: string
}): Promise<VoiceMessage> {
	const { message } = await luniVoiceMessagePublish({
		body: {
			hold_id: input.holdId,
			client_message_id: input.clientMessageId,
			file: new File([input.file], input.fileName, { type: input.file.type }),
		},
		fetch: deadlineFetch(PUBLISH_DEADLINE_MS),
	})
	return message
}

export async function getVoiceMessageUrl(messageId: string): Promise<VoiceUrl> {
	return luniVoiceMessageUrl({
		query: { message_id: messageId },
	})
}

export async function deleteVoiceMessage(input: {
	friendId: number
	messageId: string
}): Promise<VoiceMessage> {
	const { message } = await luniVoiceMessageDelete({
		body: { friend_id: input.friendId, message_id: input.messageId },
	})
	return message
}

export async function finishVoicePlayback(input: {
	friendId: number
	messageId: string
}): Promise<VoiceMessage> {
	const { message } = await luniVoiceMessageFinish({
		body: { friend_id: input.friendId, message_id: input.messageId },
	})
	return message
}

export function readConflictVoiceMessage(error: unknown): VoiceMessage | null {
	if (!isHttpError(error) || error.status !== 409) return null
	const body = zMessageBody.safeParse(error.info)
	if (!body.success) return null
	return body.data.message
}
